import { jsPDF } from 'jspdf';
import 'jspdf-autotable';


// Northstar Analytics Brand Colors
const deepMidnightBlue = [0, 43, 69]; // #002B45 - Primary
const tealBlue = [31, 181, 165]; // #1FB5A5 - Accent
const polarisWhite = [249, 251, 255]; // #F9FBFF - Background
const coolGraphite = [74, 74, 74]; // #4A4A4A - Text
const arcticSky = [180, 225, 224]; // #B4E1E0 - Light Accent

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })?.format(amount);
};

// Helper function to add header to each page
const addHeader = (doc) => {
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  
  // Header background
  doc?.setFillColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.rect(0, 0, pageWidth, 50, 'F');
  
  // Teal Blue accent strip
  doc?.setFillColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
  doc?.rect(0, 45, pageWidth, 5, 'F');
  
  // Company name
  doc?.setTextColor(255, 255, 255);
  doc?.setFontSize(24);
  doc?.setFont('helvetica', 'bold');
  doc?.text('Northstar Analytics', pageWidth / 2, 22, { align: 'center' });
  doc?.setFontSize(12);
  doc?.setFont('helvetica', 'normal');
  doc?.text('ROI Calculator Report', pageWidth / 2, 33, { align: 'center' });
  doc?.setFontSize(9);
  doc?.setTextColor(arcticSky?.[0], arcticSky?.[1], arcticSky?.[2]);
  doc?.text(`Generated: ${new Date()?.toLocaleDateString()}`, pageWidth / 2, 42, { align: 'center' });
};

// Helper function to add footer
const addFooter = (doc, pageNumber) => {
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  const pageHeight = doc?.internal?.pageSize?.getHeight();
  
  doc?.setFillColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.rect(0, pageHeight - 15, pageWidth, 15, 'F');
  
  doc?.setTextColor(255, 255, 255);
  doc?.setFontSize(8);
  doc?.text('Northstar Analytics | Your Guide to Data Excellence', 20, pageHeight - 7);
  doc?.text(`Page ${pageNumber}`, pageWidth - 30, pageHeight - 7);
};

// Helper function to check page break
const checkPageBreak = (doc, yPosition, requiredSpace = 20) => {
  const pageHeight = doc?.internal?.pageSize?.getHeight();
  if (yPosition + requiredSpace > pageHeight - 20) {
    doc?.addPage();
    addHeader(doc);
    return 65;
  }
  return yPosition;
};

// Helper function to add section title
const addSectionTitle = (doc, title, yPosition) => {
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  
  doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.setFontSize(16);
  doc?.setFont('helvetica', 'bold');
  doc?.text(title, 20, yPosition);
  
  // Decorative line
  doc?.setDrawColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
  doc?.setLineWidth(2);
  doc?.line(20, yPosition + 2, 20 + doc?.getTextWidth(title), yPosition + 2);
  
  return yPosition + 12;
};

// Data Strategy ROI Calculator PDF
export const generateDataStrategyPDF = (results, formData) => {
  const doc = new jsPDF();
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  let yPosition = 20;
  let pageNumber = 1;

  // Add header
  addHeader(doc);
  yPosition = 65;

  // Report Title
  doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.setFontSize(20);
  doc?.setFont('helvetica', 'bold');
  doc?.text('Data Strategy ROI Analysis', 20, yPosition);
  yPosition += 15;

  // Executive Summary Box
  doc?.setDrawColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.setLineWidth(1);
  doc?.roundedRect(20, yPosition, 170, 50, 3, 3, 'S');
  
  doc?.setFillColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
  doc?.roundedRect(21, yPosition + 1, 168, 48, 3, 3, 'F');
  
  doc?.setTextColor(255, 255, 255);
  doc?.setFontSize(16);
  doc?.setFont('helvetica', 'bold');
  doc?.text(`Annual ROI: ${results?.annualROI?.toFixed(0)}%`, 25, yPosition + 15);
  doc?.setFontSize(14);
  doc?.text(`Total Benefits: ${formatCurrency(results?.totalBenefits)}`, 25, yPosition + 28);
  doc?.text(`Payback Period: ${results?.paybackPeriod?.toFixed(1)} months`, 25, yPosition + 41);
  
  yPosition += 60;

  // Company Profile
  yPosition = addSectionTitle(doc, 'Company Profile', yPosition);
  
  const profileData = [
    ['Company Size', formData?.companySize || 'N/A'],
    ['Industry', formData?.industry || 'N/A'],
    ['Annual Revenue', formatCurrency(parseFloat(formData?.annualRevenue) || 0)],
    ['Data Team Size', formData?.dataTeamSize || 'N/A'],
    ['Current Data Maturity', formData?.currentDataMaturity || 'N/A']
  ];
  
  doc?.autoTable({
    startY: yPosition,
    head: [['Attribute', 'Value']],
    body: profileData,
    theme: 'grid',
    headStyles: {
      fillColor: deepMidnightBlue,
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [249, 251, 255]
    },
    margin: { left: 20, right: 20 }
  });
  
  yPosition = doc?.lastAutoTable?.finalY + 15;
  yPosition = checkPageBreak(doc, yPosition, 80);

  // Financial Analysis
  yPosition = addSectionTitle(doc, 'Financial Analysis', yPosition);
  
  const financialData = [
    ['Implementation Cost', formatCurrency(results?.implementationCost)],
    ['Operational Efficiency Gains', formatCurrency(results?.efficiencyGains)],
    ['Faster Decision Making', formatCurrency(results?.decisionSpeedUp)],
    ['Risk Reduction', formatCurrency(results?.riskReduction)],
    ['Cost Optimization', formatCurrency(results?.costSavings)],
    ['Total Annual Benefits', formatCurrency(results?.totalBenefits)],
    ['Monthly Benefit', formatCurrency(results?.monthlyBenefit)]
  ];
  
  doc?.autoTable({
    startY: yPosition,
    head: [['Metric', 'Value']],
    body: financialData,
    theme: 'grid',
    headStyles: {
      fillColor: tealBlue,
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [249, 251, 255]
    },
    margin: { left: 20, right: 20 }
  });
  
  yPosition = doc?.lastAutoTable?.finalY + 15;
  yPosition = checkPageBreak(doc, yPosition, 100);

  // Strategic Recommendations
  yPosition = addSectionTitle(doc, 'Strategic Recommendations', yPosition);
  
  doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
  doc?.setFontSize(11);
  doc?.setFont('helvetica', 'normal');
  
  const recommendations = [
    {
      title: '1. Establish Data Governance Framework',
      description: 'Implement comprehensive data governance policies to ensure data quality, security, and compliance. This foundation will enable 25-30% improvement in data reliability and reduce operational risks by up to 40%.',
      impact: 'Expected Impact: 25-30% improvement in data reliability'
    },
    {
      title: '2. Invest in Analytics Infrastructure',
      description: 'Deploy modern analytics platforms and tools to enable self-service analytics across the organization. This will accelerate decision-making cycles by 50-60% and empower teams with real-time insights.',
      impact: 'Expected Impact: 50-60% faster decision-making cycles'
    },
    {
      title: '3. Build Data Literacy Programs',
      description: 'Develop comprehensive training programs to enhance data literacy across all organizational levels. Improved data skills will drive 35-45% increase in data-driven decision adoption and ROI realization.',
      impact: 'Expected Impact: 35-45% increase in data-driven decisions'
    },
    {
      title: '4. Implement Advanced Analytics',
      description: 'Leverage predictive analytics and machine learning to uncover hidden patterns and opportunities. Organizations typically see 40-50% improvement in forecasting accuracy and 30-35% reduction in operational costs.',
      impact: 'Expected Impact: 40-50% improvement in forecasting accuracy'
    }
  ];
  
  recommendations?.forEach((rec, index) => {
    yPosition = checkPageBreak(doc, yPosition, 40);
    
    doc?.setFontSize(12);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
    doc?.text(rec?.title, 20, yPosition);
    yPosition += 7;
    
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    const descLines = doc?.splitTextToSize(rec?.description, 170);
    doc?.text(descLines, 20, yPosition);
    yPosition += descLines?.length * 5 + 5;
    
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'italic');
    doc?.setTextColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.text(rec?.impact, 20, yPosition);
    yPosition += 12;
  });

  // Add footer to all pages
  const totalPages = doc?.internal?.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc?.setPage(i);
    addFooter(doc, i);
  }

  // Save the PDF
  doc?.save('Data-Strategy-ROI-Report.pdf');
};

// Analytics Readiness Checker PDF
export const generateAnalyticsReadinessPDF = (results, formData) => {
  const doc = new jsPDF();
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  let yPosition = 20;
  let pageNumber = 1;

  // Add header
  addHeader(doc);
  yPosition = 65;

  // Report Title
  doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.setFontSize(20);
  doc?.setFont('helvetica', 'bold');
  doc?.text('Analytics Readiness Assessment', 20, yPosition);
  yPosition += 15;

  // Overall Score Box
  const scoreColor = results?.readinessLevel === 'High' ? [34, 197, 94] : 
                     results?.readinessLevel === 'Medium' ? [245, 158, 11] : [239, 68, 68];
  
  doc?.setDrawColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.setLineWidth(1);
  doc?.roundedRect(20, yPosition, 170, 50, 3, 3, 'S');
  
  doc?.setFillColor(scoreColor?.[0], scoreColor?.[1], scoreColor?.[2]);
  doc?.roundedRect(21, yPosition + 1, 168, 48, 3, 3, 'F');
  
  doc?.setTextColor(255, 255, 255);
  doc?.setFontSize(18);
  doc?.setFont('helvetica', 'bold');
  doc?.text(`Readiness Score: ${results?.readinessPercentage}%`, 25, yPosition + 15);
  doc?.setFontSize(14);
  doc?.text(`Level: ${results?.readinessLevel}`, 25, yPosition + 28);
  doc?.setFontSize(11);
  doc?.setFont('helvetica', 'normal');
  doc?.text(results?.recommendation, 25, yPosition + 41);
  
  yPosition += 60;

  // Organization Profile
  yPosition = addSectionTitle(doc, 'Organization Profile', yPosition);
  
  const profileData = [
    ['Company Size', formData?.companySize || 'N/A'],
    ['Industry', formData?.industry || 'N/A'],
    ['Data Quality', formData?.dataQuality || 'N/A'],
    ['Team Skills', formData?.teamSkills || 'N/A'],
    ['Budget', formData?.budget || 'N/A'],
    ['Timeline', formData?.timeline || 'N/A']
  ];
  
  doc?.autoTable({
    startY: yPosition,
    head: [['Attribute', 'Value']],
    body: profileData,
    theme: 'grid',
    headStyles: {
      fillColor: deepMidnightBlue,
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [249, 251, 255]
    },
    margin: { left: 20, right: 20 }
  });
  
  yPosition = doc?.lastAutoTable?.finalY + 15;
  yPosition = checkPageBreak(doc, yPosition, 80);

  // Readiness Breakdown
  yPosition = addSectionTitle(doc, 'Readiness Breakdown', yPosition);
  
  const categoryLabels = {
    size: 'Organization Size',
    quality: 'Data Quality',
    skills: 'Team Skills',
    tools: 'Current Tools',
    budget: 'Budget Allocation',
    objectives: 'Objectives Clarity'
  };
  
  const categoryData = Object.entries(results?.categoryScores)?.map(([key, data]) => [
    categoryLabels?.[key],
    `${data?.score}/${data?.max}`,
    `${data?.percentage}%`
  ]);
  
  doc?.autoTable({
    startY: yPosition,
    head: [['Category', 'Score', 'Percentage']],
    body: categoryData,
    theme: 'grid',
    headStyles: {
      fillColor: tealBlue,
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [249, 251, 255]
    },
    margin: { left: 20, right: 20 }
  });
  
  yPosition = doc?.lastAutoTable?.finalY + 15;
  yPosition = checkPageBreak(doc, yPosition, 60);

  // Improvement Plan
  yPosition = addSectionTitle(doc, 'Improvement Plan', yPosition);
  
  const improvementData = [
    ['Estimated Investment', formatCurrency(results?.improvementCost)],
    ['Time to Full Readiness', `${results?.timeToReadiness} months`],
    ['Priority', results?.priority]
  ];
  
  doc?.autoTable({
    startY: yPosition,
    head: [['Metric', 'Value']],
    body: improvementData,
    theme: 'grid',
    headStyles: {
      fillColor: [245, 158, 11],
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [249, 251, 255]
    },
    margin: { left: 20, right: 20 }
  });
  
  yPosition = doc?.lastAutoTable?.finalY + 15;
  yPosition = checkPageBreak(doc, yPosition, 100);

  // Actionable Recommendations
  yPosition = addSectionTitle(doc, 'Actionable Recommendations', yPosition);
  
  doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
  doc?.setFontSize(11);
  doc?.setFont('helvetica', 'normal');
  
  const recommendations = [
    {
      title: '1. Enhance Data Quality Management',
      description: 'Implement automated data quality monitoring and cleansing processes. Establish data quality metrics and SLAs to ensure consistent, reliable data across all analytics initiatives. This will improve analytics accuracy by 40-50% and reduce time spent on data preparation by 60%.',
      impact: 'Expected Impact: 40-50% improvement in analytics accuracy'
    },
    {
      title: '2. Upskill Analytics Team',
      description: 'Develop comprehensive training programs covering modern analytics tools, statistical methods, and data visualization best practices. Invest in certifications and hands-on workshops to build advanced analytics capabilities within your team.',
      impact: 'Expected Impact: 35-45% increase in team productivity'
    },
    {
      title: '3. Modernize Analytics Infrastructure',
      description: 'Upgrade to cloud-based analytics platforms that support scalability, real-time processing, and advanced analytics capabilities. Implement self-service analytics tools to democratize data access across the organization.',
      impact: 'Expected Impact: 50-60% reduction in time-to-insight'
    },
    {
      title: '4. Establish Analytics Governance',
      description: 'Create clear analytics governance policies covering data access, security, and usage guidelines. Define roles and responsibilities for analytics initiatives and establish a center of excellence to drive best practices and standards.',
      impact: 'Expected Impact: 30-40% improvement in analytics adoption'
    }
  ];
  
  recommendations?.forEach((rec, index) => {
    yPosition = checkPageBreak(doc, yPosition, 40);
    
    doc?.setFontSize(12);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
    doc?.text(rec?.title, 20, yPosition);
    yPosition += 7;
    
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    const descLines = doc?.splitTextToSize(rec?.description, 170);
    doc?.text(descLines, 20, yPosition);
    yPosition += descLines?.length * 5 + 5;
    
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'italic');
    doc?.setTextColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.text(rec?.impact, 20, yPosition);
    yPosition += 12;
  });

  // Add footer to all pages
  const totalPages = doc?.internal?.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc?.setPage(i);
    addFooter(doc, i);
  }

  // Save the PDF
  doc?.save('Analytics-Readiness-Report.pdf');
};

// Compliance Calculator PDF
export const generateCompliancePDF = (results, formData) => {
  const doc = new jsPDF();
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  let yPosition = 20;
  let pageNumber = 1;

  // Add header
  addHeader(doc);
  yPosition = 65;

  // Report Title
  doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.setFontSize(20);
  doc?.setFont('helvetica', 'bold');
  doc?.text('Compliance Cost Analysis', 20, yPosition);
  yPosition += 15;

  // Executive Summary Box
  doc?.setDrawColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.setLineWidth(1);
  doc?.roundedRect(20, yPosition, 170, 50, 3, 3, 'S');
  
  doc?.setFillColor(239, 68, 68);
  doc?.roundedRect(21, yPosition + 1, 168, 48, 3, 3, 'F');
  
  doc?.setTextColor(255, 255, 255);
  doc?.setFontSize(14);
  doc?.setFont('helvetica', 'bold');
  doc?.text(`Potential Penalties: ${formatCurrency(results?.potentialPenalties)}`, 25, yPosition + 13);
  doc?.text(`First Year Investment: ${formatCurrency(results?.totalFirstYear)}`, 25, yPosition + 26);
  doc?.text(`ROI: ${results?.roi > 0 ? '+' : ''}${results?.roi?.toFixed(0)}%`, 25, yPosition + 39);
  
  yPosition += 60;

  // Company Profile
  yPosition = addSectionTitle(doc, 'Company Profile', yPosition);
  
  const profileData = [
    ['Company Size', formData?.companySize || 'N/A'],
    ['Industry', formData?.industry || 'N/A'],
    ['Data Volume', formData?.dataVolume || 'N/A'],
    ['Current Compliance Level', formData?.currentCompliance || 'N/A'],
    ['Regulations', formData?.regulations?.join(', ') || 'N/A'],
    ['Geographic Scope', formData?.geographicScope?.join(', ') || 'N/A']
  ];
  
  doc?.autoTable({
    startY: yPosition,
    head: [['Attribute', 'Value']],
    body: profileData,
    theme: 'grid',
    headStyles: {
      fillColor: deepMidnightBlue,
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [249, 251, 255]
    },
    margin: { left: 20, right: 20 }
  });
  
  yPosition = doc?.lastAutoTable?.finalY + 15;
  yPosition = checkPageBreak(doc, yPosition, 80);

  // Cost Breakdown
  yPosition = addSectionTitle(doc, 'Cost Breakdown', yPosition);
  
  const costData = [
    ['Implementation Cost', formatCurrency(results?.implementationCost)],
    ['Annual Maintenance', formatCurrency(results?.annualMaintenance)],
    ['Total First Year Cost', formatCurrency(results?.totalFirstYear)],
    ['Potential Annual Penalties', formatCurrency(results?.potentialPenalties)],
    ['Cost Avoidance', formatCurrency(results?.costAvoidance)],
    ['Net Benefit', formatCurrency(results?.netBenefit)]
  ];
  
  doc?.autoTable({
    startY: yPosition,
    head: [['Cost Item', 'Amount']],
    body: costData,
    theme: 'grid',
    headStyles: {
      fillColor: tealBlue,
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [249, 251, 255]
    },
    margin: { left: 20, right: 20 }
  });
  
  yPosition = doc?.lastAutoTable?.finalY + 15;
  yPosition = checkPageBreak(doc, yPosition, 60);

  // Risk Analysis
  yPosition = addSectionTitle(doc, 'Risk Analysis', yPosition);
  
  const riskData = [
    ['Risk Reduction', `${results?.riskReduction?.toFixed(0)}%`],
    ['Payback Period', `${results?.paybackPeriod?.toFixed(1)} months`],
    ['Return on Investment', `${results?.roi?.toFixed(0)}%`]
  ];
  
  doc?.autoTable({
    startY: yPosition,
    head: [['Metric', 'Value']],
    body: riskData,
    theme: 'grid',
    headStyles: {
      fillColor: [239, 68, 68],
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [249, 251, 255]
    },
    margin: { left: 20, right: 20 }
  });
  
  yPosition = doc?.lastAutoTable?.finalY + 15;
  yPosition = checkPageBreak(doc, yPosition, 100);

  // Compliance Recommendations
  yPosition = addSectionTitle(doc, 'Compliance Recommendations', yPosition);
  
  doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
  doc?.setFontSize(11);
  doc?.setFont('helvetica', 'normal');
  
  const recommendations = [
    {
      title: '1. Implement Comprehensive Data Governance',
      description: 'Establish a robust data governance framework covering data classification, access controls, retention policies, and audit trails. This foundation will ensure 50-60% improvement in compliance readiness and reduce audit preparation time by 70%.',
      impact: 'Expected Impact: 50-60% improvement in compliance readiness'
    },
    {
      title: '2. Deploy Automated Compliance Monitoring',
      description: 'Implement automated compliance monitoring tools to continuously track regulatory requirements, detect violations, and generate compliance reports. This will reduce manual compliance efforts by 60-70% and improve response time to regulatory changes by 80%.',
      impact: 'Expected Impact: 60-70% reduction in manual compliance efforts'
    },
    {
      title: '3. Establish Privacy-by-Design Practices',
      description: 'Integrate privacy and compliance considerations into all data processes from the outset. Implement data minimization, purpose limitation, and consent management practices to reduce compliance risks by 45-55% and build customer trust.',
      impact: 'Expected Impact: 45-55% reduction in compliance risks'
    },
    {
      title: '4. Conduct Regular Compliance Audits',
      description: 'Schedule quarterly internal compliance audits and annual third-party assessments to identify gaps and ensure ongoing compliance. Regular audits will improve compliance posture by 40-50% and reduce penalty risks by 65-75%.',
      impact: 'Expected Impact: 65-75% reduction in penalty risks'
    }
  ];
  
  recommendations?.forEach((rec, index) => {
    yPosition = checkPageBreak(doc, yPosition, 40);
    
    doc?.setFontSize(12);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
    doc?.text(rec?.title, 20, yPosition);
    yPosition += 7;
    
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    const descLines = doc?.splitTextToSize(rec?.description, 170);
    doc?.text(descLines, 20, yPosition);
    yPosition += descLines?.length * 5 + 5;
    
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'italic');
    doc?.setTextColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.text(rec?.impact, 20, yPosition);
    yPosition += 12;
  });

  // Add footer to all pages
  const totalPages = doc?.internal?.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc?.setPage(i);
    addFooter(doc, i);
  }

  // Save the PDF
  doc?.save('Compliance-Cost-Analysis-Report.pdf');
};